import {isObject, isUndefined} from 'lodash';

import {globalWindow} from '../../helpers/browser/browserHelpers';
import {FrontConfig} from '../../helpers/bundler/bundlerDefineConfig';
import {isDevelopment, isPreview, isProduction} from '../../helpers/environment/envHelpers';
import {isMobile} from '../../helpers/environment/mobileEnvHelpers';
import {TaskSourcesEnum} from '../../helpers/instrumentation/eventLoopPerformanceHelpers';
import {
  computeIsGlobalNamespace,
  isTeammateNamespace,
  isTeamNamespace,
} from '../../models/helpers/namespaceHelpers';
import {SettingsTrackingTypesEnum} from '../../models/tracking/contexts/settingsTracking';
import {DataEventActionsEnum} from '../../models/tracking/dataEventActions';
import {AnyDataEventContext} from '../../models/tracking/dataEventContexts';
import {ActionBase} from '../../store/actionTypes';
import {CommonAppState} from '../../store/commonAppState';
import {CommonAppStore} from '../../store/commonAppStore';
import {reportToBugsnag} from '../bugsnag/bugsnag';
import {postAsync} from '../http/http';

globalWindow.shouldLogInteractionTracking = FrontConfig.DUMP_TRACKING_EVENTS_TO_CONSOLE;

// Uncomment to log all events. Alternatively just set the `DUMP_TRACKING_EVENTS_TO_CONSOLE` env variable.
// globalWindow.shouldLogInteractionTracking = true;

/*
 * Types.
 */

declare global {
  interface Window {
    shouldLogInteractionTracking?: boolean;
  }
}

export enum TrackingTypesEnum {
  COMPOSER_INTEGRATION = 'composer_integration',
  ELECTRON = 'electron',
  FEATURE_DISCOVER = 'feature_discover',
  FLAG_REQUESTED = 'flag_requested',
  FRONTEND_EVENT = 'frontend_event',
  INTEGRATION_OPEN = 'integration_open',
  INTEGRATION_MOUSE_ENTER = 'integration_mouse_enter',
  INTEGRATION_MOUSE_LEAVE = 'integration_mouse_leave',
  INTEGRATION_CLOSE = 'integration_close',
  INTERFACE_EVENT = 'interface_event',
  ONBOARDING_STEP = 'onboarding_step',
  PLUGIN_API = 'plugin_api',
  UI_SDK_API = 'ui_sdk_api',
  CLIENT_COUNTERS_CHECK = 'client_counters_check',
}

export enum InterfaceEventsEnum {
  ADD_NEW_INBOX = 'add_new_inbox',
  ADD_NEW_VIEW = 'add_new_view',
  ADD_QUICK_RULE = 'add_quick_rule',
  ADD_REMOVE_TAG = 'add_remove_tag',
  CELLS_FILTERS_SELECTED = 'p2.inbox_tab.select',
  CHANNEL_MASS_ARCHIVE_EVENT = 'channel_mass_archive_event',
  CLICK_CLONE = 'click_clone',
  CLICK_CONVERSATION_DETAILS = 'click_conversation_details',
  CLICK_EDIT_SHARED_DRAFT = 'click_edit_shared_draft',
  CLICK_GCAL_RSVP_LINK = 'click_gcal_rsvp_link',
  CLICK_REPLY = 'click_reply',
  CLICK_TAG_WORKSPACE = 'open_tag_workspace',
  CLICK_TEAMMATE_P1 = 'click_teammate_p1',
  CLICK_LENS_P1 = 'click_lens',
  CLICK_TOGGLE_TAG_WORKSPACE = 'toggle_tag_workspace',
  COMMENT_ADD_ZOOM = 'comment_add_zoom',
  CONVERSATION_CELL_DRAG_AND_DROP_TO_EML = 'conversation_cell_drag_and_drop_to_eml',
  DESTROY_MESSAGE = 'destroy_message',
  DRAG_AND_DROP_TO_PRIVATE_TAG = 'drag_and_drop_to_private_tag',
  DUPLICATE_CONVERSATION_BANNER_ACTION = 'duplicate_conversation_banner_action',
  EDIT_VIEW = 'edit_view',
  FOLLOW_CONVERSATION = 'follow_conversation',
  IDLE_RELOAD = 'idle_reload',
  INTEGRATION_INFORMATION_BANNER_ACTION = 'integration_information_banner_action',
  MERGE_DUPLICATE_CONVERSATIONS_MANUALLY = 'merge_duplicate_conversations_manually',
  MESSAGE_DRAG_AND_DROP_TO_EML = 'message_drag_and_drop_to_eml',
  MESSAGE_CELL_DRAG_AND_DROP_TO_EML = 'message_cell_drag_and_drop_to_eml',
  MESSAGE_SENT_LATER = 'message_sent_later',
  MOVE_MENU = 'move_menu',
  OPEN_EML_FILE = 'open_eml_file',
  OPEN_HELP = 'open_help',
  OPEN_MESSAGE_SEND_OPTIONS = 'open_message_send_options',
  OPEN_WORKSPACE_MENU_ROW = 'open_workspace_row',
  PIN_TAG_TO_TEAMMATE_WORKSPACES = 'pin_tag_to_teammate_workspaces',
  POPOUT_VIEWER = 'popout_p3',
  POPOUT_MESSAGE = 'popout_message',
  RECIPIENTS_AUTOSUGGEST_CLICK = 'recipients_autosuggest_click',
  SAVE_EML = 'save_eml',
  SELECT_FONT_FAMILY = 'font_family_select',
  SELECT_FONT_SIZE = 'font_size_select',
  SHARE_MENU = 'share_menu',
  SNOOZE_CONVERSATION = 'snooze_conversation',
  SPLIT_CONVERSATION = 'split_conversation',
  SPLIT_CONVERSATION_BANNER_ACTION = 'split_conversation_banner_action',
  SYSTEM_DARK_MODE_CHANGE = 'system.dark_mode.change',
  // Low and medium priority activities in P3.
  TIMELINE_ACTIVITY_ACTION = 'timeline_activity_action',
  WHATS_NEW = 'whats_new',

  // Search.
  SEARCH_SESSION_START = 'search_session_start',
  SEARCH_SESSION_END = 'search_session_end',
  SEARCH_DROPDOWN_CLICK = 'search_dropdown_click',
  SEARCH_DROPDOWN_EXPAND = 'search_dropdown_expand',
  SEARCH_QUERY = 'search_query',
  SEARCH_P2_CONVERSATION_CLICK = 'search_p2_conversation_click',
  SEARCH_HINT_CLICK = 'search_hint_click',
  SEARCH_HINT_MORE_FILTERS_CLICK = 'search_hint_more_filters_click',
  SEARCH_HINT_DROPDOWN_EXPAND = 'search.hint_dropdown.expand',
  SEARCH_QUERY_BUILDER_CLICK = 'search_query_builder_click',
  SEARCH_QUERY_BUILDER_RESET_CLICK = 'search_query_builder_reset_click',
  SEARCH_QUERY_BUILDER_SEARCH_CLICK = 'search_query_builder_search_click',
  SEARCH_NO_RESULTS_HELPFUL_TIP_CLICK = 'search_no_result_helpful_tip_click',
  SEARCH_NO_RESULTS_NUDGE_CLICK = 'search_no_results_nudge_click',
  SEARCH_CLIENT_SIDE_FILTERING_DISCREPANCY = 'search_client_side_filtering_discrepancy',
  SEARCH_SUGGESTIONS_BULK_LATENCY = 'search_suggestions_bulk_latency',
  P3_CONVERSATION_SEARCH_CLICK = 'p3.conversation_search.click',

  // In-app demo scheduling.
  DEMO_SCHEDULING_SUCCESS = 'demo_scheduling_success',
  DEMO_SCHEDULING_FAILURE = 'demo_scheduling_failure',
  DEMO_SCHEDULING_SHOWN = 'demo_scheduling_shown',
  DEMO_SCHEDULING_BANNER_SHOWN = 'demo_scheduling_banner_shown',
  DEMO_SCHEDULING_CARD_CLOSED = 'demo_scheduling_card_closed',

  // Message composer.
  COMPOSER_CHANNEL_CHANGE = 'conversation.composer.switch_sender',
  COMPOSER_BANNER_VIEW = 'conversation.composer.banner_view',
  COMPOSER_BANNER_CLICK = 'conversation.composer.banner_click',
  COMPOSER_INSERT = 'conversation.composer.insert',
  COMPOSER_TOOLTIP_VIEW = 'conversation.composer.tooltip_view',
  COMPOSER_TOOLTIP_CLICK = 'conversation.composer.tooltip_click',
  COMPOSER_MODAL_VIEW = 'conversation.composer.modal_view',
  COMPOSER_MODAL_CLICK = 'conversation.composer.modal_click',

  // Comment composer.
  COMMENT_COMPOSER_EXPAND_ICON_CLICK = 'comment_composer.expand_icon.click',
  COMMENT_COMPOSER_MENTION_ICON_CLICK = 'comment_composer.mention_icon.click',
  COMMENT_COMPOSER_EMOJI_ICON_CLICK = 'comment_composer.emoji_icon.click',
  COMMENT_COMPOSER_PLUS_ICON_CLICK = 'comment_composer.plus_icon.click',
  COMMENT_COMPOSER_ZOOM_ICON_CLICK = 'comment_composer.zoom_icon.click',
  COMMENT_COMPOSER_ADD_FILES_ICON_CLICK = 'comment_composer.add_files_icon.click',
  COMMENT_COMPOSER_FORMATTING_ADD = 'comment_composer.formatting.add',
  COMMENT_COMPOSER_SEND_ICON_CLICK = 'comment_composer.send_icon.click',
  COMMENT_COMPOSER_GIF_ICON_CLICK = 'comment_composer.gif_icon.click',
  COMMENT_COMPOSER_GIF_ICON_SEND = 'comment_composer.gif_icon.send',

  // P3 Recent communications / conversations banner
  P3_RECENT_CONVERSATIONS_BANNER_VIEW = 'p3.recent_conversations_banner.view',
  P3_RECENT_CONVERSATIONS_BANNER_CLICK = 'p3.recent_conversations_banner.click',

  // P3 true select all.
  P3_MULTI_SELECT_MODAL_PERFORM_ACTION = 'p3.multi_select_modal.perform_action',
  P3_MULTI_SELECT_MODAL_CLICK = 'p3.multi_select_modal.click',
  P3_MULTI_SELECT_MODAL_WARNING_DISPLAYED = 'p3.multi_select_modal.warning_displayed',

  // P3 toolbar.
  P3_ADD_TAG_USING_DROPDOWN = 'p3.action_dropdown.tag',
  P3_NAVIGATE_TAG_DROPDOWN = 'p3.action_navigate_dropdown.tag',
  P3_REMOVE_TAG_USING_HEADER = 'p3.conversation_tag.remove',
  P3_ASSIGN_USING_DROPDOWN = 'p3.action_dropdown.assign',
  P3_ADD_DESCRIPTION_ON_DROPDOWN = 'p3.action_add_description.tag',
  P3_HEADER_EXPAND_CHEVRON_CLICK = 'p3.header_expand_chevron.click',

  // P3 activities.
  P3_ADD_TAG_USING_ACTIVITY = 'p3.activity.tag',
  P3_REMOVE_TAG_USING_ACTIVITY = 'p3.activity.untag',

  // P4 contact details.
  P4_CONTACT_DETAILS_VIEW = 'p4.contact_details.view',
  P4_CONTACT_DETAILS_EDIT = 'p4.contact_details.edit',
  P4_CONTACT_DETAILS_OPEN_CONVERSATION = 'p4.contact_details.open_conversation',
  P4_CONTACT_DETAILS_ADD_GROUP = 'p4.contact_details.add_group',
  P4_CONTACT_DETAILS_SWITCH_CONTACT = 'p4.contact_details.switch_contact',
  P4_CONTACT_DETAILS_EDIT_NOTE = 'p4.contact_details.edit_note',
  P4_CONTACT_DETAILS_EDIT_CONTACT_GROUP = 'p4.contact_details.edit_contact_group',
  P4_CONTACT_DETAILS_CLICK = 'p4.contact_details.click',
  P4_CONTACT_DETAILS_ADD_TO_CONTACTS = 'p4.contact_details.add_to_contacts',
  P4_CONTACT_DETAILS_MERGE = 'p4.contact_details.merge',
  P4_CONTACT_DETAILS_ERROR_DISPLAYED = 'p4.contact_details.error_displayed',
  P4_CONTACT_DETAILS_HOVER = 'p4.contact_details.hover',
  P4_CONTACT_DETAILS_SWITCH_TABS = 'p4.contact_details.switch_tabs',
  P4_CONTACT_DETAILS_EDIT_ACCOUNT = 'p4.contact_details.edit_account',
  P4_CONTACT_DETAILS_CONVERT_CONTACT_TYPE = 'p4.contact_details.convert_contact_type',
  P4_CONTACT_DETAILS_SYNC_CONTACT = 'p4.contact_details.sync_contact',

  // Contact card.
  CONTACT_CARD_VIEW = 'contact_card.details.view',
  CONTACT_CARD_NOTES_CLICK = 'contact_card.notes_link.click',
  CONTACT_CARD_CONVERSATIONS_CLICK = 'contact_card.recent_conversations.click',

  // New user onboarding.
  INFO_TIP_VIEW = 'feature_tip.screen.view',
  HELP_CENTER_VIEW = 'p4.getting_started.view',
  HELP_CENTER_TASK_CLICK = 'p4.getting_started.click',
  HELP_CENTER_EXPAND = 'p4.getting_started_icon.expand',
  HELP_CENTER_TASKS_COMPLETED = 'p4.getting_started.completed',
  HELP_CENTER_P1_REMINDER_VIEW = 'p1.reminder_banner.view',
  HELP_CENTER_P1_REMINDER_CLICK = 'p1.reminder_banner.click',
  HELP_CENTER_P4_REMINDER_VIEW = 'p4.reminder_banner.view',
  HELP_CENTER_P4_REMINDER_CLICK = 'p4.reminder_banner.click',
  HELP_CENTER_P4_SEARCH = 'p4.help_center.search',
  HELP_CENTER_P4_SEARCH_CLICK = 'p4.help_center.click',
  TRIAL_UPGRADE_CLICK = 'trial.upgrade.click',
  BANNER_CHECKLIST_CLICK = 'banner.checklist.click',
  BANNER_CHECKLIST_SHOW = 'banner.checklist.show',
  PO_GETTING_STARTED_CLICK = 'p0.getting_started_click',
  P0_GETTING_STARTED_OPEN = 'p0.getting_started_open',
  P0_GETTING_STARTED_CLOSE = 'p0.getting_started_close',
  P0_GETTING_STARTED_SUCCESS = 'p0.getting_started_success',
  EMPTY_STATE_EXPLORE_SANDBOX_CLICK = 'empty_state.explore_sandbox.click',
  EMPTY_STATE_GENERATE_NEW_DEMO_EMAIL_CLICK = 'empty_state.generate_new_demo_email.click',
  EMPTY_STATE_REMOVE_FROM_SIDEBAR_CLICK = 'empty_state.remove_from_sidebar.click',
  P0_LEAVE_SANDBOX_CLICK = 'p0.leave_sandbox.click',
  LEAVE_SANDBOX_CONFIRM_CLICK = 'leave_sandbox.confirm.click',

  // LMS tracking
  P0_LMS_CLICK = 'p0.lms.click',
  P4_LMS_CLICK = 'p4.lms.click',
  SETTINGS_LMS_CLICK = 'settings.lms.click',

  // Help center entry point tracking
  P0_HELP_CENTER_CLICK = 'p0.help_center.click',

  // Conversation Summary + Pinned Comments
  CONVERSATION_SUMMARY_SUMMARY_VIEW = 'conversation_summary.summary.view',
  CONVERSATION_SUMMARY_SECTION_VIEW = 'conversation_summary.section.view',
  CONVERSATION_SUMMARY_COMMENT_PIN_ICON_CLICK = 'conversation_summary.comment_pin_icon.click',
  CONVERSATION_SUMMARY_PINNED_COMMENT_CLICK = 'conversation_summary.pinned_comment.click',
  P3_COMMENT_PIN_ICON_CLICK = 'p3.comment_pin_icon.click',

  // Conversation Preview
  P3_CONVERSATION_PREVIEW_CLICK = 'p3.conversation_preview.click',
  P3_CONVERSATION_PREVIEW_VIEW = 'p3.conversation_preview.view',

  // Scheduling links.
  SCHEDULING_P4_VIEW = 'p4.scheduling.view',
  SCHEDULING_P4_COPY_URL = 'p4.scheduling.copy_url',
  SCHEDULING_P4_CREATE = 'p4.scheduling.create',
  SCHEDULING_P4_UPDATE = 'p4.scheduling.update',
  SCHEDULING_P4_DELETE = 'p4.scheduling.delete',
  SCHEDULING_COMPOSER_VIEW = 'composer.scheduling.view',
  SCHEDULING_COMPOSER_COPY_URL = 'composer.scheduling.copy_url',
  SCHEDULING_COMPOSER_CREATE = 'composer.scheduling.create',
  SCHEDULING_COMPOSER_UPDATE = 'composer.scheduling.update',
  SCHEDULING_COMPOSER_DELETE = 'composer.scheduling.delete',
  SCHEDULING_COMPOSER_INSERT = 'composer.scheduling.insert',

  // Canned answers.
  CANNED_ANSWER_CREATE = 'composer.canned_answer.create',
  SETTINGS_CANNED_ANSWER_CREATE = 'settings.canned_answer.create',
  SETTINGS_CANNED_ANSWER_UPDATE = 'settings.canned_answer.update',
  SETTINGS_CANNED_ANSWER_DELETE = 'settings.canned_answer.delete',
  SETTINGS_CANNED_ANSWER_MOVE = 'settings.canned_answer.move',
  CANNED_ANSWER_INSERT = 'composer.canned_answer.insert',

  SETTINGS_CANNED_ANSWER_FOLDER_CREATE = 'settings.canned_answer_folder.create',
  SETTINGS_CANNED_ANSWER_FOLDER_UPDATE = 'settings.canned_answer_folder.update',
  SETTINGS_CANNED_ANSWER_FOLDER_DELETE = 'settings.canned_answer_folder.delete',
  SETTINGS_CANNED_ANSWER_FOLDER_MOVE = 'settings.canned_answer_folder.move',

  // Analytics
  ANALYTICS_DOCUMENTATION_CLICK = 'analytics.documentation.click',
  ANALYTICS_HELP_CLICK = 'analytics.help.click',
  ANALYTICS_NAMESPACE_SELECTED = 'analytics.namespace.selected',

  ANALYTICS_TOOLTIP_CTA_CLICK = 'analytics.tooltip_cta.click',

  ANALYTICS_VIEW_SCHEDULE = 'analytics.view.schedule',
  ANALYTICS_VIEW_EXPORT = 'analytics.view.export',
  ANALYTICS_VIEW_EXPORT_CSAT = 'analytics.view.export_csat',
  ANALYTICS_VIEW_EXPORT_METRIC_DETAILS = 'analytics.view.export_metric_details',
  ANALYTICS_VIEW_EXPORT_TABLE = 'analytics.view.export_table',
  ANALYTICS_VIEW_EXPORT_CHART = 'analytics.view.export_chart',

  ANALYTICS_ADD_VIEW_CLICK = 'analytics.add_view.click',
  ANALYTICS_SAVE_VIEW_CLICK = 'analytics.save_view.click',
  ANALYTICS_VIEW_CREATE = 'analytics.view.create',
  ANALYTICS_EDIT_VIEW_CLICK = 'analytics.edit_view.click',
  ANALYTICS_VIEW_EDIT = 'analytics.view.edit',
  ANALYTICS_VIEW_CLOSE = 'analytics.view.close',

  ANALYTICS_ADD_FILTER_CLICK = 'analytics.add_filter.click',
  ANALYTICS_FILTER_SELECT = 'analytics.filter.select',
  ANALYTICS_FILTER_EDIT = 'analytics.filter.edit',
  ANALYTICS_FILTER_REMOVE = 'analytics.filter.remove',
  ANALYTICS_RESET_FILTER_CLICK = 'analytics.reset_filter.click',
  ANALYTICS_DATE_RANGE_CLICK = 'analytics.date_range.click',
  ANALYTICS_DATE_RANGE_SELECT = 'analytics.date_range.select',
  ANALYTICS_METRIC_SELECT = 'analytics.metric.select',

  ANALYTICS_METRIC_LIST_CLICK = 'analytics.metric_list.click',
  ANALYTICS_METRIC_LIST_EDIT = 'analytics.metric_list.edit',
  ANALYTICS_COLUMN_LIST_EDIT = 'analytics.column_list.edit',
  ANALYTICS_COMPARE_PREVIOUS_CLICK = 'analytics.compare_previous.click',
  ANALYTICS_DETAILS_DRAWER_OPEN = 'analytics.details_drawer.open',
  ANALYTICS_DETAILS_DRAWER_CLICK = 'analytics.details_drawer.click',

  ANALYTICS_VIEW_OPEN = 'analytics.view.open',
  ANALYTICS_ACTION_MENU_CLICK = 'analytics.action_menu.click',
  ANALYTICS_DUPLICATE_VIEW_CLICK = 'analytics.duplicate_view.click',
  ANALYTICS_TABLE_FILTER = 'analytics.table.filter',
  ANALYTICS_FILTER_CLICK = 'analytics.filter.click',
  ANALYTICS_SORT_VALUES_CLICK = 'analytics.sort_values.click',

  // Help widget events.
  HELP_WIDGET_HELP_CENTER_CLICK = 'settings_help_widget_help_center_click',
  HELP_WIDGET_SUPPORT_CLICK = 'settings_help_widget_support_click',
  HELP_WIDGET_RESOURCES_CLICK = 'settings_help_widget_resources_click',
  HELP_WIDGET_OPEN_CLICK = 'settings_help_widget_open_click',

  SETTINGS_SEARCH_QUERY = 'settings.search_bar.query',
  SETTINGS_SEARCH_CLICK = 'settings.search_bar.result_click',

  SETTINGS_PAGE_VIEW = 'settings.page.view',
  SETTINGS_PAGE_DISPLAY_NUDGE = 'settings.page.display_nudge',
  SETTINGS_PAGE_SHOW_ME_NUDGE = 'settings.page.show_me_nudge',
  SETTINGS_PAGE_LEARN_MORE_NUDGE = 'settings.page.learn_more_nudge',
  SETTINGS_PAGE_CLICK_TO_SORT = 'settings.page.click_to_sort',
  SETTINGS_PAGE_TOGGLE_INFO_BAR = 'settings.page.toggle_info_bar',

  SETTINGS_TAG_UPDATE = 'settings.tag.update',
  SETTINGS_TAG_CREATE = 'settings.tag.create',
  SETTINGS_TAG_DELETE = 'settings.tag.delete',
  TAG_EDIT_TAG_DELETE = 'tag_edit.tag.delete',
  SETTINGS_TAG_CLICK = 'settings.tag.click',
  SETTINGS_TAG_ARCHIVE = 'settings.tag.archive',
  TAG_EDIT_TAG_ARCHIVE = 'tag_edit.tag.archive',
  SETTINGS_TAG_NEST = 'settings.tag.nest',
  SETTINGS_TAG_SEARCH = 'settings.tag.search',
  SETTINGS_TAG_RESTORE = 'settings.tag.restore',
  SETTINGS_TAG_SORT_BY = 'settings.tag.sort_by',

  /*
   * Pinned workspace events.
   */
  /** When the user pins a newly created teammate to other workspaces. */
  SETTINGS_PIN_TAG_TO_TEAMMATE_WORKSPACES = 'settings_pin_tag_to_teammate_workspaces',
  /** When the user pins a newly created teammate to other workspaces. */
  SETTINGS_PIN_TEAMMATE_TO_OTHER_WORKSPACES = 'settings_pin_teammate_to_other_workspaces',

  SETTINGS_RULE_ADD = 'settings.rule.add',
  SETTINGS_RULE_CREATE = 'settings.rule.create',
  SETTINGS_RULE_UPDATE = 'settings.rule.update',
  SETTINGS_RULE_DELETE = 'settings.rule.delete',
  SETTINGS_RULE_VIEW = 'settings.rule.view',
  SETTINGS_RULE_LIST_FILTER = 'settings.rule.filter_list',

  SETTINGS_DYNAMIC_VARIABLE_CREATE_CLICK = 'settings.dynamic_variable.create_click',
  SETTINGS_DYNAMIC_VARIABLE_LEARN_MORE_CLICK = 'settings.dynamic_variable.learn_more_click',
  SETTINGS_DYNAMIC_VARIABLE_CREATE = 'settings.dynamic_variable.create',
  SETTINGS_DYNAMIC_VARIABLE_EDIT = 'settings.dynamic_variable.edit',

  // P0.
  NAVIGATION_CLICK = 'app.navigation.click',
  USER_PROFILE_CLICK = 'app.user_profile_avatar.click',
  P0_CONTACTS_MANAGER_SEARCH = 'p0.contacts_manager.search',

  // P1 reorder
  P1_WORKSPACE_REORDER = 'p1.workspace_display.reorder',
  P1_TAG_UPDATE_NEST = 'p1.tag.update_nest',
  P1_WORKSPACE_CHEVRON_CLICK = 'p1.workspace_display.chevron_click',
  P1_MORE_SECTION_CHEVRON_CLICK = 'p1.more_section_display.chevron_click',
  P1_PINNED_WORKSPACE_CHEVRON_CLICK = 'p1.pinned_workspace_display.chevron_click',
  P1_TAG_CHEVRON_CLICK = 'p1.tag.chevron_click',
  SETTINGS_WORKSPACE_DISPLAY_REORDER = 'settings.workspace_display.reorder',

  // P1 edit sidebar mode
  P1_EDIT_REORDER = 'p1.edit_sidebar.reorder',
  P1_EDIT_NEST = 'p1.edit_sidebar.nest',
  P1_EDIT_UPDATE = 'p1.edit_sidebar.update',
  P1_EDIT_SELECT = 'p1.edit_sidebar.select',
  P1_EDIT_VIEW = 'p1.edit_sidebar.view',
  P1_WORKSPACE_CLICK = 'p1.workspace_display.click',
  P1_WORKSPACE_VIEW = 'p1.workspace_display.view',
  P1_EDIT_CANCEL = 'p1.edit_sidebar.cancel',
  P1_EDIT_HOVER = 'p1.edit_sidebar.hover',

  // P2 tracking
  P2_CELL_CLICK = 'p2.conversation_cell.click',
  P2_CELL_DRAG = 'p2.conversation_cell.drag',
  P2_CELL_TIMESTAMP_TOGGLE = 'p2.cell_timestamp.toggle',
  P2_FILTER_OPEN = 'p2.filter.open',
  P2_FILTER_APPLY = 'p2.filter.apply',
  P2_FILTER_APPLIED = 'p2.filter.applied',
  P2_FILTER_REMOVE = 'p2.filter.remove',

  // Demo inbox/cell tracking
  P2_DEMO_CELL_OPEN = 'p2.conversation_cell.demo.click',

  // Contacts Manager
  CONTACTS_MANAGER_CONTACTS_SECTION_VIEW = 'contacts_manager.contacts_section.view',
  CONTACTS_MANAGER_CARD_GROUPS_SECTION_VIEW = 'contacts_manager.lists_section.view',
  CONTACTS_MANAGER_CONTACT_DETAILS_VIEW = 'contacts_manager.contact_details.view',
  CONTACTS_MANAGER_CONTACT_EDIT = 'contacts_manager.contact.edit',
  CONTACTS_MANAGER_CONTACT_CREATE = 'contacts_manager.contact.create',
  CONTACTS_MANAGER_CONTACT_DELETE = 'contacts_manager.contact.delete',
  CONTACTS_MANAGER_CONTACT_MERGE = 'contacts_manager.contact.merge',
  CONTACTS_MANAGER_CONTACT_MESSAGE = 'contacts_manager.contact.message',
  CONTACTS_MANAGER_CONTACT_CARD_GROUP_EDIT = 'contacts_manager.contact.list_edit',
  CONTACTS_MANAGER_CONTACT_IMPORT = 'contacts_manager.contact.import',
  CONTACTS_MANAGER_CARD_GROUP_CREATE = 'contacts_manager.lists.create',
  CONTACTS_MANAGER_CARD_GROUP_DELETE = 'contacts_manager.lists.delete',
  CONTACTS_MANAGER_CARD_GROUP_UPDATE = 'contacts_manager.lists.update',
  CONTACTS_MANAGER_INFO_BOX_CLICK = 'contacts_manager.info_box.click',

  // Contacts Manager - CRM Sync
  CONTACTS_MANAGER_CRM_SYNC_VIEW = 'contacts_manager.crm_sync.view',
  CONTACTS_MANAGER_CRM_SYNC_DYNAMICS_INVALID_URL = 'contacts_manager.crm_sync.dynamics.invalid_url',
  CONTACTS_MANAGER_CRM_SYNC_NOW_CLICK = 'contacts_manager.crm_sync_now.click',

  // Contacts Manager - Accounts Manager
  CONTACTS_MANAGER_ACCOUNTS_VIEW = 'accounts_manager.accounts_list.view',
  CONTACTS_MANAGER_ACCOUNT_VIEW = 'accounts_manager.account.view',
  CONTACTS_MANAGER_ACCOUNT_CREATE = 'accounts_manager.account.create',
  CONTACTS_MANAGER_ACCOUNT_EDIT = 'accounts_manager.account.edit',
  CONTACTS_MANAGER_ACCOUNT_DELETE = 'accounts_manager.account.delete',
  CONTACTS_MANAGER_ACCOUNT_COPY_LINK = 'accounts_manager.account.copy_link',
  CONTACTS_MANAGER_ACCOUNT_IMPORT_SUCCESS = 'accounts_manager.account.import',
  CONTACTS_MANAGER_ACCOUNT_IMPORT_ERROR = 'accounts_manager.import.error',

  // Comment on message
  P3_MESSAGE_TEXT_HIGHLIGHT = 'p3.message_text.highlight',
  P3_MESSAGE_POPOVER_CLICK = 'p3.message_popover.click',
  P3_MESSAGE_COMMENT_CLICK = 'p3.message_comment.click',
  P3_MESSAGE_COMMENT_PREVIEW = 'p3.message_comment.preview',
  P3_MESSAGE_TEXT_COMMENT = 'p3.message_text.comment',

  // Link Tracking
  P3_MESSAGE_LINK_CLICK = 'p3.message_link.click',
  P3_COMMENT_LINK_CLICK = 'p3.comment_link.click',
  AI_HARVESTING_REVIEW_LINK_CLICK = 'p3.ai_harvesting_review_link.click',

  // AI Tagging report
  P3_AI_REPORT_INACCURATE_TAG_CLICK = 'p3.ai_report_inaccurate_tag.click',

  // Events for adding the survey template variable.
  SETTINGS_CANNED_ANSWER_ADD_SURVEY = 'settings.canned_answer.add_survey',
  SETTINGS_SIGNATURE_ADD_SURVEY = 'settings.signature.add_survey',

  SETTINGS_SIGNATURE_CREATE = 'settings.signature.create',
  SETTINGS_SIGNATURE_UPDATE = 'settings.signature.update',
  SETTINGS_SIGNATURE_DELETE = 'settings.signature.delete',

  /** When a user views the variable dropdown when editing a signature */
  SETTINGS_SIGNATURE_VARIABLE_MENU_VIEW = 'settings.signature.variable_menu_view',
  /** When a user selects a value from the variable dropdown when editing a signature */
  SETTINGS_SIGNATURE_VARIABLE_MENU_SELECT = 'settings.signature.variable_menu_select',
  /** When a user views the variable dropdown when editing a canned answer */
  SETTINGS_CANNED_ANSWER_VARIABLE_MENU_VIEW = 'settings.canned_answer.variable_menu_view',
  /** When a user selects a value from the variable dropdown when editing a canned answer */
  SETTINGS_CANNED_ANSWER_VARIABLE_MENU_SELECT = 'settings.canned_answer.variable_menu_select',

  SETTINGS_INVITE_TEAMMATE_CLICK = 'settings.invite_teammate.click',
  SETTINGS_HOMEPAGE_CLICK = 'settings.homepage.click',
  COMPOSER_COPIED_MESSAGE_INSERT = 'composer.copied_message.insert',
  COMMENT_COMPOSER_COMMENT_REACT = 'comment_composer.comment.react',
  COMPOSER_REPEAT_CLICK = 'composer.repeat.click',

  // Rule Table Events
  SETTINGS_RULE_TABLE_CREATE_CLICK = 'settings.rule_table.create_click',
  SETTINGS_RULE_TABLE_OPEN_TABLE = 'settings.rule_table.open_table',
  SETTINGS_RULE_TABLE_EXPORT = 'settings.rule_table.export',
  SETTINGS_RULE_TABLE_IMPORT = 'settings.rule_table.import',
  SETTINGS_RULE_TABLE_IMPORT_STARTED = 'settings.rule_table.import_started',
  SETTINGS_RULE_TABLE_IMPORT_COMPLETED = 'settings.rule_table.import_completed',
  SETTINGS_RULE_TABLE_FIRST_NEW_ROW = 'settings.rule_table.first_new_row',
  SETTINGS_RULE_TABLE_LEARN_MORE_CLICK = 'settings.rule_table.learn_more_click',
  SETTINGS_RULE_TABLE_ADD_COLUMN = 'settings.rule_table.add_column',
  SETTINGS_RULE_TABLE_ADD_ROW = 'settings.rule_table.add_row',
  SETTINGS_RULE_TABLE_CREATED = 'settings.rule_table.table_created',
  SETTINGS_RULE_TABLE_EDITED = 'settings.rule_table.table_edited',
  SETTINGS_RULE_TABLE_TOGGLE_DRY_RUN = 'settings.rule_table.toggle_dry_run',

  /** When a user uses the search in a preferences page */
  SEARCH_IN_PREFERENCES = 'settings_preferences_search',

  /** When a user toggles conversation cell indicator preferences (workspace/inbox indicators) */
  SETTINGS_CONVERSATION_CELL_INDICATOR_TOGGLE = 'settings.conversation_cell_indicator.toggle',

  SETTINGS_INBOX_VIEW = 'settings.inbox.view',
  SETTINGS_INBOX_CREATE = 'settings.inbox.create',
  SETTINGS_INBOX_UPDATE = 'settings.inbox.update',
  SETTINGS_INBOX_DELETE = 'settings.inbox.delete',

  SETTINGS_CHANNEL_CREATE = 'settings.channel.create',
  SETTINGS_CHANNEL_UPDATE = 'settings.channel.update',
  SETTINGS_CHANNEL_DELETE = 'settings.channel.delete',

  /*
   * Inbox graph events.
   */

  INBOX_OVERVIEW_GRAPH_EXPAND = 'settings.inbox_rules.graph.expand',
  INBOX_OVERVIEW_GRAPH_CLICK = 'settings.inbox_rules.graph.click',
  INBOX_SEE_RULES_CLICK = 'settings.inbox_see_rules.click',
  INBOX_SEE_CHANNELS_CLICK = 'settings.inbox_see_channels.click',

  /*
   * New inbox flow events.
   */
  /** When the user selects a rule type during inbox creation */
  RULE_TYPE_CHOSEN = 'settings_rule_type_chosen',
  /** When the user creates a new rule */
  RULE_CREATED = 'settings_rule_created',
  /** When the user completes the rule selection form */
  RULE_SETUP_COMPLETED = 'settings_rule_setup_completed',

  /** When the user completes the channel selection form */
  CHANNEL_SETUP_COMPLETED = 'settings_channel_setup_completed',

  /** When the user completes the inbox settings form */
  INBOX_SETTINGS_FORM_COMPLETED = 'settings_inbox_settings_form_completed',

  // Access page events.
  /** When the teammate toggles the autojoin option. */
  INBOX_AUTOJOIN_TOGGLED = 'settings_inbox_autojoin_toggled',
  /** When the user manually modifies the selection of teammate IDs for an inbox. */
  INBOX_TEAMMATE_IDS_MODIFIED = 'settings_inbox_teammate_ids_modified',
  /** When the user clicks Create inbox. */
  INBOX_ACCESS_FORM_COMPLETED = 'settings_inbox_access_form_completed',
  /** When the user toggles the import history option for Gmail/Office365 channels. */
  IMPORT_HISTORY_TOGGLED = 'settings_import_history_toggled',
  /** When the user toggles the sync tags option for Gmail/Office365 channels. */
  SYNC_TAGS_TOGGLED = 'settings_sync_tags_toggled',

  /*
   * Events tracked after channel has been created. The forward settings page for
   * SMTP channels falls under this category, since it's only shown once the
   * channel has actually been created.
   */

  // Events specific to SMTP email channel validation.
  /** When the user first clicks Send test email. */
  SMTP_VALIDATION_STARTED = 'settings_smtp_validation_started',
  /** When the test email is sent. */
  SMTP_VALIDATION_EMAIL_SENT = 'settings_smtp_validation_email_sent',
  /** When the test email takes too long and times out. */
  SMTP_VALIDATION_TIMED_OUT = 'settings_smtp_validation_timed_out',
  /** When the test email is successfully sent. */
  SMTP_VALIDATION_SUCCEEDED = 'settings_smtp_validation_succeeded',
  /** When user confirms leaving the page before finishing validation. */
  SMTP_VALIDATION_SKIP_CONFIRMED = 'settings_smtp_validation_skip_confirmed',

  /** When the Gmail/Office 365 OAuth popup is closed. Does not necessarily mean OAuth succeeded or failed. */
  OAUTH_POPUP_CLOSED = 'settings_oauth_popup_closed',
  /** When we try to open the OAuth popup window but the browser blocks it. */
  OAUTH_POPUP_BLOCKED = 'settings_oauth_popup_blocked',
  /** When our wait for Gmail/Office 365 OAuth success times out and we show the help banner. */
  OAUTH_SUCCESS_TIMED_OUT = 'settings_oauth_success_timed_out',
  /** When we show the help banner despite Gmail/Office 365 OAuth completing successfully. */
  OAUTH_SUCCESS_TIMED_OUT_ERRONEOUSLY = 'settings_oauth_success_timed_out_erroneously',

  /** On the channel edit page, when the user confirms sending the validation email. */
  SMTP_VALIDATION_CONFIRMED = 'settings_smtp_validation_confirmed',
  /** On the channel edit page, when the user clicks cancel when the send validation email dialog pops up. */
  SMTP_VALIDATION_CANCELED = 'settings_smtp_validation_canceled',

  /** When the user is linked to the create inbox page from the client to force a Google OAuth flow. */
  GOOGLE_OAUTH_FLOW_TRIGGERED = 'settings_google_oauth_flow_triggered',
  /** When the user is linked to the create inbox page from the client to force an Office365 OAuth flow. */
  OFFICE365_OAUTH_FLOW_TRIGGERED = 'settings_office365_oauth_flow_triggered',

  /*
   * Channel events.
   */

  SETTINGS_CHANNEL_VIEW = 'settings.channel.view',

  /*
   * Channel creator events.
   */

  /** Events associated with completing a step. */
  CHANNEL_CREATOR_ACCOUNT_TYPE_STEP_COMPLETED = 'settings_channel_creator_account_type_step_completed',
  CHANNEL_CREATOR_EMAIL_INPUT_STEP_COMPLETED = 'settings_channel_creator_email_input_step_completed',
  CHANNEL_CREATOR_CONNECTION_STEP_COMPLETED = 'settings_channel_creator_connection_step_completed',
  CHANNEL_CREATOR_SETTINGS_STEP_COMPLETED = 'settings_channel_creator_settings_step_completed',

  /** Events associated with DNS verification. */
  CHANNEL_CREATOR_DNS_VERIFICATION_STARTED = 'settings_channel_creator_dns_verification_started',
  CHANNEL_CREATOR_DNS_VERIFICATION_SKIPPED = 'settings_channel_creator_dns_verification_skipped',
  CHANNEL_CREATOR_DNS_VERIFICATION_SUCCEEDED = 'settings_channel_creator_dns_verification_succeeded',
  CHANNEL_CREATOR_DNS_VERIFICATION_FAILED = 'settings_channel_creator_dns_verification_failed',
  CHANNEL_CREATOR_CONTACT_SUPPORT_CLICK = 'settings_channel_creator_contact_support_click',

  /** Events associated with custom smtp setup in the channel creator.. */
  CHANNEL_CREATOR_CUSTOM_SMTP_STARTED = 'settings_channel_creator_custom_smtp_started',
  CHANNEL_CREATOR_CUSTOM_SMTP_SKIPPED = 'settings_channel_creator_custom_smtp_skipped',
  CHANNEL_CREATOR_CUSTOM_SMTP_SUCCEEDED = 'settings_channel_creator_custom_smtp_succeeded',
  CHANNEL_CREATOR_CUSTOM_SMTP_FAILED = 'settings_channel_creator_custom_smtp_failed',
  CHANNEL_CREATOR_CUSTOM_SMTP_TIMED_OUT = 'settings_channel_creator_custom_smtp_timeout',

  /*
   * Events tracked during channel creation.
   */

  // General channel creation events.
  /** When the user chooses the empty inbox option. */
  EMPTY_FOLDER_OPTION_CHOSEN = 'settings_empty_folder_option_chosen',
  /** When the user chooses a channel type to create (e.g. Google). */
  CHANNEL_TYPE_CHOSEN = 'settings_channel_type_chosen',
  /** When the user chooses a channel subtype (e.g. Google Group). */
  CHANNEL_SUBTYPE_CHOSEN = 'settings_channel_subtype_chosen',
  /** When the user has finished setting up all channel options and we are about to hit the backend to create the channel. */
  CHANNEL_READY_FOR_CREATION = 'settings_channel_ready_for_creation',
  /** When a channel is created at the end of the flow. */
  CHANNEL_CREATED = 'settings_channel_created',
  /** When an inbox is created at the end of the flow. */
  INBOX_CREATED = 'settings_inbox_created',
  /** When something goes wrong when creating the channel/inbox and we delete the channel we've already created. */
  CHANNEL_CREATION_ROLLBACK = 'settings_channel_creation_rollback',

  // Events specific to the email channel creation flow.
  /** When the user completes the email form. */
  EMAIL_FORM_COMPLETED = 'settings_email_form_completed',
  /** When the OAuth authentication succeeds. */
  OAUTH_ACCOUNT_SAVED = 'settings_oauth_account_saved',
  /** When the OAuth account authenticated already exists for the company. */
  OAUTH_ACCOUNT_ALREADY_EXISTS = 'settings_oauth_account_already_exists',
  /** When the email filled out earlier is different from the OAuth-ed account's email. */
  OAUTH_ACCOUNT_ADDRESS_MISMATCH = 'settings_oauth_account_address_mismatch',
  /** When the user overrides our alert to continue using the mismatched address for the channel. */
  OAUTH_ACCOUNT_ADDRESS_MISMATCH_OVERRIDDEN = 'settings_oauth_account_address_mismatch_overridden',
  /** When the user decides to not proceed with the mismatched channel and OAuth addresses. */
  OAUTH_ACCOUNT_ADDRESS_MISMATCH_CANCELED = 'settings_oauth_account_address_mismatch_canceled',
  /** When the user pins the newly created inbox to other workspaces. */
  SETTINGS_PIN_INBOX_TO_TEAMMATE_WORKSPACES = 'settings_pin_inbox_to_teammate_workspaces',

  /** When the user clicks Add inbox on one of the inbox list pages. */
  ADD_INBOX_CLICKED = 'settings_add_inbox_clicked',

  /** When the user clicks Add a Channel in the side menu of an existing inbox's settings page. */
  ADD_CHANNEL_CLICKED = 'settings_add_channel_clicked',

  CONVERSATION_SHARED_DRAFT_VIEW = 'conversation.shared_draft.view',
  // Applications (P4 and App Store)

  APPS_P4_SEARCH = 'p4.manager.search',
  APPS_P4_ALL_PLUGINS_CLICK = 'p4.manager.click',
  APPS_P4_PIN = 'p4.manager.pin',
  APPS_P4_UNPIN = 'p4.manager.unpin',
  APPS_SETTINGS_ENABLE_WORKFLOW = 'integration-enable-workflow',
  APPS_SETTINGS_ENABLE_COMPLETED = 'integration-enable-completed',
  APPS_SETTINGS_UPDATE_COMPLETED = 'app-settings.integration_updated',
  APPS_SETTINGS_LEARN_MORE = 'app-settings.integration_learn_more',
  APPS_SETTINGS_DISABLE_COMPLETED = 'integration-disable-completed',
  APPS_SETTINGS_SEARCH = 'app-settings.search',
  APPS_SETTINGS_CLICK = 'app-settings.click',
  APPS_SETTINGS_CATEGORY_CLICK = 'app-settings.categories.click',
  APPS_SETTINGS_PAGE_VIEW = 'app-settings.page.view',
  APPS_SETTINGS_START_USING = 'integration-enable-completed.start_using',
  APPS_SETTINGS_BANNER_VIEW = 'integration-enable-completed.popup_view',
  APPS_SETTINGS_BANNER_CLOSE = 'integration-enable-completed.popup_close',
  APPS_SETTINGS_BANNER_OPEN_PLUGIN = 'integration-enable-completed.popup_click',

  // Application Context
  APPLICATION_CONTEXT_API_BASE = 'context_API',
  APPLICATION_CONTEXT_UPDATE = 'context_API.contextUpdates',

  // Quick Insert
  QUICK_INSERT_OPEN = 'quick-insert.open',
  QUICK_INSERT_CANCEL = 'quick-insert.cancel',
  QUICK_INSERT_BACK = 'quick-insert.back',

  // Billing
  DOWNSELL_SURVEY = 'downsell_survey',

  EMPTY_STATE_NON_ADMIN_REQUEST_UPGRADE_CLICK = 'empty_state.non_admin_request_upgrade.click',
  EMPTY_STATE_LEARN_MORE_CLICK = 'empty_state.learn_more.click',
  EMPTY_STATE_ADMIN_UPGRADE_CLICK = 'empty_state.admin_upgrade.click',

  UPGRADE_CARD_MODAL_VIEW_ALL_PLANS_CLICK = 'upgrade_card_modal.view_all_plans.click',
  UPGRADE_CARD_MODAL_CONTACT_US_CLICK = 'upgrade_card_modal.contact_us.click',
  UPGRADE_CARD_MODAL_BOOK_A_CALL_CLICK = 'upgrade_card_modal.book_a_call.click',
  UPGRADE_CARD_MODAL_CANCEL_CLICK = 'upgrade_card_modal.cancel.click',
  UPGRADE_CARD_MODAL_CONTINUE_TO_CHECKOUT_CLICK = 'upgrade_card_modal.continue_to_checkout.click',
  UPGRADE_CARD_MODAL_TRIAL_FEATURE_CLICK = 'upgrade_card_modal.trial_feature.click',

  UPGRADE_CONTACT_US_SUCCESS_POPUP = 'upgrade_contact_us_success_popup',

  UPGRADE_TOOLTIP_VIEW_PLANS_CLICK = 'upgrade_tooltip.view_plans.click',
  UPGRADE_TOOLTIP_ADMIN_UPGRADE_BUTTON_CLICK = 'upgrade_tooltip.admin_upgrade_button.click',
  UPGRADE_TOOLTIP_CONTACT_US_CLICK = 'upgrade_tooltip.contact_us.click',
  UPGRADE_TOOLTIP_REQUEST_UPGRADE_CLICK = 'upgrade_tooltip.request_upgrade.click',
  UPGRADE_TOOLTIP_REQUEST_ADD_ON_CLICK = 'upgrade_tooltip.request_add_on.click',
  UPGRADE_TOOLTIP_GET_ADD_ON_CLICK = 'upgrade_tooltip.get_add_on.click',

  UPGRADE_ADD_ON_MODAL_CANCEL_CLICK = 'upgrade_add_on_modal.cancel.click',
  UPGRADE_ADD_ON_MODAL_CONTINUE_TO_CHECKOUT_CLICK = 'upgrade_add_on_modal.continue_to_checkout.click',

  NONADMIN_UPGRADE_REQUEST_CANCEL_CLICK = 'nonadmin_upgrade_request.cancel.click',
  NONADMIN_UPGRADE_REQUEST_CONTINUE_CLICK = 'nonadmin_upgrade_request.continue.click',

  MONTHLY_TO_ANNUAL_AGREEMENT_CLICK = 'monthly_to_annual_agreement.click',

  SETTINGS_BILLING_CONTACT_US_CLICK = 'settings.billing.contact_us.click',

  SETTINGS_BILLING_VIEW_ALL_FEATURES_CLICK = 'settings.view_all_plan_features.click',
  SETTINGS_BILLING_TOGGLE_SELECT = 'settings.toggle.select',
  SETTINGS_BILLING_CHOOSE_PLAN_CLICK = 'settings.choose_plan.click',
  SETTINGS_BILLING_CONFIRM_PLAN_CLICK = 'settings.confirm_plan.click',
  SETTINGS_BILLING_CONFIRM_PLAN_CONTINUE_CLICK = 'settings.confirm_plan_continue.click',
  SETTINGS_BILLING_CONFIRM_PURCHASE_CLICK = 'settings.confirm_purchase.click',

  // AI Compose
  AI_COMPOSE_OPEN = 'composer.ai_compose.open',
  AI_COMPOSE_SELECT = 'composer.ai_compose.select',
  AI_COMPOSE_SUCCESS = 'composer.ai_compose.success',
  AI_COMPOSE_INSERT = 'composer.ai_compose.insert',
  AI_COMPOSE_SEND_SIMILARITY = 'composer.ai_compose.send_similarity',
  AI_COMPOSE_SEND = 'composer.ai_compose.send',
  AI_COMPOSE_UNDO = 'composer.ai_compose_undo.select',
  COMPOSER_FLOATING_TOOLBAR_VIEW = 'composer.floating_toolbar.view',

  // AI Compose Assistant (POC)
  AI_COMPOSE_ASSISTANT_INPUT_CLICK = 'composer.ai_compose_assistant_input.click',
  AI_COMPOSE_ASSISTANT_CANCEL_CLICK = 'composer.ai_compose_assistant_cancel.click',
  AI_COMPOSE_ASSISTANT_ERROR_VIEW = 'composer.ai_compose_assistant_error.view',
  AI_COMPOSE_ASSISTANT_ACCEPT_CLICK = 'composer.ai_compose_assistant_accept.click',
  AI_COMPOSE_ASSISTANT_DISMISS_CLICK = 'composer.ai_compose_assistant_dismiss.click',
  AI_COMPOSE_ASSISTANT_RETRY_CLICK = 'composer.ai_compose_assistant_retry.click',

  // AI Suggestion (aka AI Answers for Email)
  P3_AI_SUGGEST_CLICK = 'p3.ai_suggest.click',
  P3_AI_SUGGEST_HOVER = 'p3.ai_suggest.hover',
  P3_AI_SUGGEST_SOURCE_CLICK = 'p3.ai_suggest_source.click',

  // Decision Trees and Chatbots
  SETTINGS_CHAT_BOTS_LIST_VIEW = 'settings.chatbots.list.view',
  SETTINGS_CHAT_BOTS_TAB_CLICK = 'settings.chatbots.tab.click',
  SETTINGS_CHAT_BOTS_CREATE_CLICK = 'settings.chatbots.create.click',
  SETTINGS_CHAT_BOTS_CREATE_SAVE_CLICK = 'settings.chatbots.create.save.click',
  SETTINGS_CHAT_BOTS_BUILDER_SAVE_CLICK = 'settings.chatbots.builder.save.click',
  SETTINGS_CHAT_BOTS_BUILDER_PUBLISH_CLICK = 'settings.chatbots.builder.publish.click',
  SETTINGS_CHAT_BOTS_EDIT_SAVE_CLICK = 'settings.chatbots.edit.save.click',
  SETTINGS_CHAT_BOTS_ACTIVE_TOGGLE_CLICK = 'settings.chatbots.active_toggle.click',
  SETTINGS_CHAT_BOTS_DISABLE_CLICK = 'settings.chatbots.disable.click',
  SETTINGS_CHAT_BOTS_SET_LIVE_CLICK = 'settings.chatbots.set_live.click',
  SETTINGS_CHAT_BOTS_PATH_ANALYSIS_CLICK = 'settings.chatbots.path_analysis.click',

  // Window and panels metrics
  WINDOW_METRICS = 'system.window_metrics',

  // Knowledge base
  KNOWLEDGE_BASE_ARTICLE_INSERT = 'composer.knowledge_base_article.insert',
  KNOWLEDGE_BASE_SETTINGS = 'knowledge_base.settings',
  KNOWLEDGE_BASE_PUBLISH = 'knowledge_base.settings.publish',
  KNOWLEDGE_BASE_UNPUBLISH = 'knowledge_base.settings.unpublish',
  KNOWLEDGE_BASE_LIST_CONTENT = 'knowledge_base.list_content',
  KNOWLEDGE_BASE_IMPORT_VIEW = 'knowledge_base.import.view',
  KNOWLEDGE_BASE_IMPORT_ERRORS_VIEW = 'knowledge_base.import-errors.view',
  KNOWLEDGE_BASE_IMPORT_ERRORS_DISMISS_CLICK = 'knowledge_base.import-errors.dismiss.click',
  KNOWLEDGE_BASE_IMPORT_FILE_UPLOADED = 'knowledge_base.import.file.uploaded',
  KNOWLEDGE_BASE_IMPORT_FILE_UPLOADED_ERROR = 'knowledge_base.import.file.uploaded-error',
  KNOWLEDGE_BASE_IMPORT_CLICK = 'knowledge_base.import.click',

  // AI Settings
  SETTINGS_AI_SAVE_SUCCESS = 'settings.ai_save.success',
  SETTINGS_AI_TERMS_LEARN_MORE_CLICK = 'settings.ai_terms_learn_more.click',
  SETTINGS_OPEN_AI_TAGGING_CREATOR_CLICK = 'settings.open_ai_tagging_creator.click',
  SETTINGS_SETUP_AIA_IN_CHATBOTS_CLICK = 'settings.setup_aia_in_chatbots.click',
}

export enum SurveyTypesEnum {
  CSAT_FIVE_STAR = 'csat_five_star',
  CSAT_THUMBS = 'csat_thumbs',
}

export enum ComposerVariableTypesEnum {
  DEFAULT = 'default',
  CUSTOM = 'custom',
}

export enum PopoutLocationsEnum {
  MORE_MENU = 'More menu',
  DOUBLE_CLICK_P2 = 'Double click P2',
  COMMENT_UNFURL = 'Comment unfurl',
  P4_PLUGIN_SDK = 'P4 plugin sdk',
  CONVERSATION_SUMMARY = 'Conversation summary',
  COMPOSE = 'Compose',
}

export enum ReplyLocationsEnum {
  AUTO = 'Auto',
  BOTTOM_OF_CONVERSATION = 'Bottom of conversation',
  KEYBOARD = 'Keyboard',
  MESSAGE_HEADER = 'Message header',
  PLUGIN_API = 'Plugin API',
  THREE_DOTS = 'Three dots',
  TOP_LEFT_BUTTON = 'Top left button',
  DRAFT_BANNER_PRIVATE_REPLY_TOOLTIP = 'Draft banner private reply tooltip',
  SHARED_DRAFT_BANNER_PRIVATE_REPLY_TOOLTIP = 'Shared Draft banner private reply tooltip',
  AI_SUGGESTION = 'AI Suggestion',
}

export enum SnoozeLocationsEnum {
  ACTIVITY_LINE = 'Activity line',
  TOPBAR = 'Snooze dropdown',
  COMPOSER = 'Reminder dropdown',
  SEND_AND_SNOOZE = 'Send and snooze',
}

export enum ChangeCalendarFocusedDayMethodsEnum {
  TODAY_BUTTON = 'Today button',
  PREVIOUS_BUTTON = 'Previous button',
  NEXT_BUTTON = 'Next button',
  DAY_PICKER = 'Day picker',
}

export enum CalendarTypesEnum {
  LARGE = 'large',
  MOBILE = 'mobile',
  SIDE = 'side',
}

export enum CalendarModalTypesEnum {
  RESCHEDULE = 'reschedule',
  DELETE = 'delete',
}

export enum CalendarModalActionsEnum {
  CONFIRM = 'confirm',
  CANCEL = 'cancel',
}

export enum OnboardingEventsEnum {
  STARTED = 'started',
  STARTED_PERSONNA = 'started_personna',
  COMPLETED_IDENTITY_INFO = 'completed_identity_info',
  COMPLETED_PERSONNA = 'completed_personna',
  COMPLETED = 'completed',
  ONBOARDING_QUESTIONNAIRE_COMPLETED = 'onboarding_questionnaire_completed',
}

export enum ChannelMassArchiveActionsEnum {
  DIALOG_DISMISS = 'dialog_dismiss',
  DIALOG_TRIGGER = 'dialog_trigger',
}

/** Values for the `entry_point` property of interaction events. */
export enum EventEntryPointsEnum {
  MANUAL = 'manual',
  KEYBOARD_SHORTCUT = 'keyboard_shortcut',
  MARKDOWN = 'markdown',
}

// For actions taken on activities in the P3 timeline.
export enum TimelineActivityActionsEnum {
  CLICK_GROUP_EXPAND = 'click_group_expand',
  CLICK_RULE_ARCHIVE = 'click_rule_archive',
  CLICK_RULE_ASSIGNMENT = 'click_rule_assignment',
  CLICK_RULE_FORWARD = 'click_rule_forward',
  CLICK_RULE_MOVE = 'click_rule_move',
  CLICK_RULE_REOPEN = 'click_rule_reopen',
  CLICK_RULE_TAG = 'click_rule_tag',
  CLICK_RULE_UNTAG = 'click_rule_untag',
  CLICK_RULE_UNASSIGNED = 'click_rule_unassigned',
  CLICK_RULE_TOPIC_ADDED = 'click_rule_topic_added',
  CLICK_RULE_TOPIC_REMOVED = 'click_rule_topic_removed',
  CLICK_RULE_CUSTOM_ATTRIBUTE_UPDATED = 'click_rule_custom_field_updated',
  CLICK_RULE_NOTIFY_TEAMMATE = 'click_rule_notify_teammate',
  CLICK_RULE_NOTIFY_YOU = 'click_rule_notify_you',
  CLICK_RULE_SNOOZE = 'click_rule_snooze',
  CLICK_RULE_SPLIT = 'click_rule_split',
  CLICK_RULE_ACTION_SKIPPED = 'click_rule_action_skipped',
  CLICK_RULE_SMART_QA = 'click_rule_smart_qa',
  CLICK_SPLIT_CONVERSATION_LINK_TO_ORIGINAL = 'click_split_conversation_link_to_original',
  CLICK_SPLIT_CONVERSATION_LINK_TO_NEW = 'click_split_conversation_link_to_new',
  CLICK_FRONT_CHAT_TRANSCRIPT_LINK_TO_CHAT = 'click_front_chat_transcript_link_to_chat',
  CLICK_FRONT_CHAT_TRANSCRIPT_LINK_TO_TRANSCRIPT = 'click_front_chat_transcript_link_to_transcript',
  CLICK_RULE_EXECUTE_APPLICATION_REQUEST = 'click_rule_execute_application_request',
}

// For actions taken on the recent communications banner
export enum RecentCommunicationsBannerActionsEnum {
  CLOSE = 'close',
  VIEW = 'view',
}

export enum RecentCommunicationsBannerLocationsEnum {
  WITHIN_COMPOSER = 'within_composer',
}

// For different sections in the P4 contacts plugin
export enum ConversationListSectionNamesEnum {
  LAST_7D = 'last_7d',
  NONE = 'none',
  OLDER = 'older',
}

export enum SchedulingSectionsEnum {
  SCHEDULING_LINK = 'scheduling_link',
  MEETING_TIMES = 'meeting_times',
}

export enum SchedulingLinksAccessPointsEnum {
  P4,
  COMPOSER,
}

export enum NamespaceTypesEnum {
  PRIVATE = 'private',
  TEAM = 'team',
}

export enum ConversationComposerBannerTypesEnum {
  MENTION_INSTEAD = 'mention_instead',
  MASS_TEXT = 'mass_text',
  CONFLICTING_COPY = 'conflicting_copy',
  ATTACHMENTS_UPLOADING = 'attachments_uploading',
  RAW_VARIABLES = 'raw_variables',
  APP_VARIABLES = 'app_variables',
  RAW_APP_VARIABLES = 'raw_app_variables',
}

export enum ConversationComposerWarningDialogTypesEnum {
  NO_SUBJECT = 'no_subject',
  NO_ATTACHMENTS = 'no_attachments',
  MANY_RECIPIENTS = 'many_recipients',
  FAILED_ATTACHMENTS = 'failed_attachments',
  UPLOADING_ATTACHMENTS = 'uploading_attachments',
  RAW_VARIABLES = 'raw_variables',
}

export enum SettingsTagTypesEnum {
  TEAM = 'team',
  INDIVIDUAL = 'individual',
}

export function getSettingTrackingTypeFromNamespace(namespace: string) {
  if (computeIsGlobalNamespace(namespace)) {
    return SettingsTrackingTypesEnum.COMPANY;
  }

  if (isTeamNamespace(namespace)) {
    return SettingsTrackingTypesEnum.TEAM;
  }

  return SettingsTrackingTypesEnum.PERSONAL;
}

export enum SettingsPageTypesEnum {
  PREFERENCES = 'preferences',
}

// For data tracking purpose, do not change enum.
export enum SettingsBillingSubPagesEnum {
  UPDATE_PLAN = 'Update plan',
  CHANGE_PLAN = 'Change plan',
  OVERVIEW = 'Overview',
  CUSTOMIZE_PLAN = 'Customize plan',
  CHECKOUT = 'Checkout',
  DOWNGRADE = 'Downgrade',
}

export enum SingleTagCreationEntryPointsEnum {
  SETTINGS_MENU = 'settings_menu',
  THREE_DOT_SELECTION = 'three_dot_selection',
}

export enum ConversationComposerTooltipLocationsEnum {
  DRAFT_BANNER = 'draft_banner',
}

export enum ConversationComposerTooltipActionsEnum {
  SEND_PRIVATE_REPLY = 'send_private_reply',
}

export enum ConversationComposerBannerActionsEnum {
  SHARE_DRAFT = 'share_draft',
  CLOSE = 'close',
}

export enum BulkActionsTrackingTypesEnum {
  ARCHIVE = 'archive',
  SNOOZE = 'snooze',
  UNSNOOZE = 'unsnooze',
  REOPEN = 'reopen',
  TRASH = 'trash',
  DESTROY = 'destroy',
  RESTORE = 'restore',
  MOVE = 'move',
  MERGE = 'merge',
  ASSIGN = 'assign',
  UNASSIGN = 'unassign',
  TAG = 'tag',
  UNTAG = 'untag',
  STAR = 'star',
  UNSTAR = 'unstar',
  MARK_READ = 'mark_read',
  MARK_UNREAD = 'mark_unread',
  MARK_SPAM = 'mark_spam',
  REPORT_PHISHING = 'report_phishing',
}

export enum CellsActionsEnum {
  ARCHIVE = 'archive',
  TRASH = 'trash',
  REOPEN = 'reopen',
  RESTORE = 'restore',
  MARK_READ = 'mark_read',
  MARK_UNREAD = 'mark_unread',
  SNOOZE = 'snooze',
  UNSNOOZE = 'unsnooze',
}

interface TrackingEvent {
  time: number;
  type: TrackingTypesEnum;
  name?: string;
  value?: unknown;
}

interface TrackingEventBatch {
  time: number;
  events: ReadonlyArray<TrackingEvent>;
}

/*
 * Constants.
 */

const developmentTrackingInterval = 5 * 1000; // ms
const productionTrackingInterval = isMobile() ? 15 * 1000 : 30 * 1000; // ms
const trackingInterval = isDevelopment() ? developmentTrackingInterval : productionTrackingInterval;

/*
 * API.
 */

let dataBatchEventEndpoint: string | undefined;

export function setupTracking<State extends CommonAppState, Action extends ActionBase>(
  store: CommonAppStore<State, Action>,
) {
  const shouldTrack = isProduction() || isPreview();
  if (!shouldTrack) {
    return;
  }

  dataBatchEventEndpoint = `${store.getState().data.company.url}/data_event_batch`;
}

export function trackEvent(type: TrackingTypesEnum, name?: string, value?: unknown): void {
  const event = {
    time: performance.now(),
    type,
    name,
    value,
  };

  if (globalWindow.location?.hash === '#print-tracking-events') {
    // eslint-disable-next-line no-console
    console.log('Tracking event', event.name, JSON.stringify(value, null, 2));
  }

  if (globalWindow.location?.hash === '#print-tracking-event-names') {
    // eslint-disable-next-line no-console
    console.log('Tracking event name', event.name);
  }

  if (globalWindow.shouldLogInteractionTracking) {
    // eslint-disable-next-line no-console
    console.log('Tracking event', event);
  }

  if (!dataBatchEventEndpoint) {
    return;
  }

  enqueueTrackingEvent(event);
  startReportingTrackingEvents();
}

export function trackInterfaceEvent(eventName: InterfaceEventsEnum, eventData?: unknown): void {
  trackEvent(TrackingTypesEnum.INTERFACE_EVENT, eventName, maybeAddWindowContext(eventData));
}

function maybeAddWindowContext(eventData?: unknown) {
  // React Native doesn't have window data.
  if (!globalWindow.location) {
    return eventData;
  }

  const windowContext = findLocationContext(globalWindow.location.pathname);
  if (!windowContext || !isObject(eventData)) {
    return eventData;
  }

  return {...eventData, window_context: windowContext};
}

/**
 * Builds a function to track an event according to the new [guidelines](https://docs.google.com/document/d/1dtr9YnCzgy9A4n2xPEr4ePBNyqW4iaAuIuKQpgp-Z1A).
 *
 * @param context Physical place in the product where the action is taking place.
 * @param action Action done by the user.
 * @param objectName Object the action is performed on.
 * @param objectData Extra data describing the object.
 */
export function trackDataEvent(
  context: AnyDataEventContext,
  action: DataEventActionsEnum,
  objectName: string,
  objectData: Record<string, unknown> = {},
): void {
  const eventName = `${context.name}.${formatObjectName(objectName)}.${action.toLowerCase()}`;
  trackEvent(TrackingTypesEnum.FRONTEND_EVENT, eventName, {...context.payload, ...objectData});
}

function formatObjectName(name: string) {
  // Replacing unwanted separators with "_" otherwise
  // the event name split done by the data team will stop working
  return name.replace(/[.:]/gm, '_').toLowerCase();
}

/*
 * Helpers.
 */

const locationContexts = [
  {
    type: 'teammate',
    regexp: new RegExp(`^/inboxes/teammates/`),
  },
  {
    type: 'team',
    regexp: new RegExp(`^/inboxes/teams/`),
  },
];

function findLocationContext(url: string) {
  const context = locationContexts.find((c) => c.regexp.test(url));
  return (context && context.type) || null;
}

/*
 * Batching.
 */

const trackingEventQueue: Array<TrackingEvent> = [];

function enqueueTrackingEvent(event: TrackingEvent) {
  trackingEventQueue.push(event);
}

/** How many events to send per batch. */
const dequeueEventCount = 100;

function dequeueTrackingEventBatch(): TrackingEventBatch | undefined {
  if (trackingEventQueue.length === 0) {
    return undefined;
  }

  return {
    time: performance.now(),
    events: trackingEventQueue.splice(0, dequeueEventCount),
  };
}

/* If we have more than this number of events, throw away events that fail to be posted. */
const requeueMaxEventCount = 500;

function requeueTrackingEventBatch(batch: TrackingEventBatch) {
  if (trackingEventQueue.length > requeueMaxEventCount) {
    reportToBugsnag(new Error(`[tracking] Buffer full, discarding ${batch.events.length} events`));
    return;
  }

  trackingEventQueue.splice(0, 0, ...batch.events);
}

/*
 * Reporting.
 */

let isReportingTrackingEvents = false;

function startReportingTrackingEvents() {
  if (isReportingTrackingEvents) {
    return;
  }

  let handle: number = 0;

  globalWindow.setInstrumentedInterval(() => {
    // Cancel the previous idle callback, in case it hasn't happened yet.
    globalWindow.cancelIdleCallback(handle);
    // Wait for the browser to be idle.
    handle = globalWindow.requestInstrumentedIdleCallback(reportTrackingEventsAsync, {
      source: TaskSourcesEnum.LOGGING,
    });
  }, trackingInterval);

  if (!isMobile()) {
    globalWindow.addEventListener('unload', (event) => {
      if (!dataBatchEventEndpoint) {
        return;
      }

      for (let batch = dequeueTrackingEventBatch(); batch; batch = dequeueTrackingEventBatch()) {
        const headers = {type: 'application/json'};
        const blob = new Blob([JSON.stringify(createTrackingEventPostBody(batch))], headers);
        navigator.sendBeacon(dataBatchEventEndpoint, blob);
      }
    });
  }

  isReportingTrackingEvents = true;
}

function createTrackingEventPostBody(batch: TrackingEventBatch) {
  return {
    events: batch.events.map((event) => ({
      type: event.type,
      name: event.name,
      value: !isUndefined(event.value) ? JSON.stringify(event.value) : undefined,
      time_offset: Math.round(event.time - batch.time),
    })),
  };
}

async function reportTrackingEventsAsync() {
  const batch = dequeueTrackingEventBatch();
  if (!batch) {
    return;
  }

  if (globalWindow.shouldLogInteractionTracking) {
    // eslint-disable-next-line no-console
    console.log('Tracking report', batch);
  }

  try {
    if (!dataBatchEventEndpoint) {
      return;
    }

    await postAsync(dataBatchEventEndpoint, createTrackingEventPostBody(batch));
  } catch (error) {
    requeueTrackingEventBatch(batch);
    throw error;
  }
}

export function getVisualSettings(trackingContext: {namespace?: string}, userNamespace: string) {
  if ('namespace' in trackingContext) {
    if (isTeammateNamespace(String(trackingContext.namespace))) {
      if (trackingContext.namespace !== userNamespace) {
        return 'teammate';
      }

      if (trackingContext.namespace === 'global') {
        return 'global';
      }

      return 'individual';
    }
  }

  return 'shared';
}
